import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import { logout } from "../features/auth/auth.api.js";
import type { NavGroup, NavLink } from "./nav-model.js";

function NavAnchor({ link, onGo }: { link: NavLink; onGo: () => void }) {
  const cls = `block rounded-lg px-3 py-2 text-sm transition ${
    link.sub ? "pl-6 text-stone-500" : "text-stone-700"
  } hover:bg-stone-100`;
  return (
    <Link
      // routes dynamiques → typage assoupli localement
      {...({ to: link.to, params: link.params, hash: link.hash } as any)}
      onClick={onGo}
      className={cls}
      activeProps={{ className: `${cls} bg-stone-900 !text-white` }}
      activeOptions={{ exact: true, includeHash: !!link.hash }}
    >
      {link.sub ? `— ${link.label}` : link.label}
    </Link>
  );
}

export function Drawer({
  open,
  onClose,
  groups,
  userName,
}: {
  open: boolean;
  onClose: () => void;
  groups: NavGroup[];
  userName: string;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/40"
          />
          <motion.aside
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", stiffness: 380, damping: 40 }}
            className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
              <Link to="/" onClick={onClose} className="font-serif text-lg text-stone-900">
                SmartVow
              </Link>
              <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
                ✕
              </button>
            </div>

            <nav className="sv-scroll flex-1 px-3 py-4">
              {groups.map((g, gi) => (
                <div key={gi} className="mb-4">
                  {g.to ? (
                    <Link
                      {...({ to: g.to, params: g.params } as any)}
                      onClick={onClose}
                      className="mb-1 block px-3 text-[11px] font-semibold uppercase tracking-wide text-stone-400 hover:text-stone-600"
                    >
                      {g.title}
                    </Link>
                  ) : (
                    <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wide text-stone-400">
                      {g.title}
                    </p>
                  )}
                  {g.links.map((l, li) => (
                    <NavAnchor key={li} link={l} onGo={onClose} />
                  ))}
                </div>
              ))}
            </nav>

            <div className="border-t border-stone-200 p-3">
              <p className="px-3 pb-2 text-xs text-stone-400">{userName}</p>
              <button
                onClick={() => logout()}
                className="block w-full rounded-lg px-3 py-2 text-left text-sm text-stone-700 hover:bg-stone-100"
              >
                Déconnexion
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
